{
  "invite": {
    "pageTitle": "Invitation",
    "hi": "Hi {{name}}!",
    "hiNoName": "Hello!",
    "intro": "Welcome! Use our cloud-hosted Isaac Sim to help you complete your assignment.",
    "brief": "Assignment brief",
    "facts": {
      "gpu": "You have {{hours}} hours of machine time",
      "gpuHelp": "Time counts only while the machine is running and the workspace is ready. Pause the machine to stop the timer.",
      "window": "You have {{days}} days to use the machine from your first launch",
      "windowHelp": "You can launch, pause, and resume within this window, subject to your machine time allowance. Please plan your time accordingly.",
      "files": "Files are not kept permanently",
      "filesHelp": "Keep your own copy of anything you need. Files are deleted when the machine is deleted after our review.",
      "gpuRemaining": "{{time}} of machine time left",
      "deadlineHelp": "Keep an eye on your remaining machine time and finish before this deadline. Launching again does not reset it.",
      "earliest": "Access ends at the earliest enabled time limit. Pausing the GPU does not pause the assignment window or continuous session."
    },
    "email": "Sign-in email",
    "emailHelp": "Codes are sent only to this address.",
    "sendCode": "Email me a sign-in code",
    "sending": "Sending…",
    "turnstileWait": "Checking your browser…",
    "error": {
      "expired": {
        "title": "This invitation has expired",
        "body": "Invitations are valid for a limited time. HR can issue a new link."
      },
      "revoked": {
        "title": "This invitation was withdrawn",
        "body": "The assignment is no longer available through this link."
      },
      "finished": {
        "title": "This session is closed",
        "body": "Your session has already been finished. Thank you."
      },
      "window_over": {
        "title": "The assessment window has ended",
        "body": "The assessment deadline has passed. Contact HR if you need more time."
      },
      "invalid": {
        "title": "This link is not valid",
        "body": "Check that you opened the complete link from HR."
      },
      "locked": {
        "title": "Invite link disabled",
        "body": "Too many incorrect verification codes were entered. Please contact HR through your existing email thread to request a new invite link."
      },
      "sendFailed": "We could not send the code. Try again in a moment."
    },
    "gpuIntro": "Welcome! Use your cloud GPU workspace and VS Code to complete your assignment."
  },
  "verify": {
    "pageTitle": "Sign in",
    "title": "Enter your sign-in code",
    "sentTo": "We sent a 6-digit code to {{email}}.",
    "orLink": "Or click the link in the email — it signs you in directly.",
    "spam": "Not there? Check your spam folder. The code is valid for 10 minutes.",
    "codeLabel": "6-digit code",
    "digit": "Digit {{n}}",
    "verifying": "Checking…",
    "resend": "Send a new code",
    "resendIn": "Send a new code ({{seconds}}s)",
    "resent": "A new code is on its way.",
    "attemptsLeft": "{{count}} attempts left",
    "attemptsLeft_one": "{{count}} attempt left",
    "noInvite": "Open your invite link from HR again to request a code.",
    "error": {
      "code_invalid": "That code is not right.",
      "code_expired": "This code has expired. Request a new one.",
      "code_burned": "Too many wrong attempts. Open your invite link again and request a new code.",
      "magic_invalid": "That sign-in link is no longer valid. Enter the code from the email or request a new one.",
      "turnstile_failed": "Browser check failed. Reload the page and try again."
    }
  },
  "consent": {
    "pageTitle": "Privacy agreement",
    "title": "Privacy agreement",
    "version": "Version {{version}} · effective {{date}}",
    "agree": "I agree to the processing described in the Privacy Agreement.",
    "otherLanguage": "Read in {{lang}}",
    "intro": "Please review the privacy agreement and confirm your choice to continue to your workspace.",
    "readAgreement": "Read the full privacy agreement (opens in a new tab)",
    "updated": "The privacy agreement has changed. Review the updated version and select the checkbox again.",
    "continue": "Continue"
  },
  "app": {
    "pageTitle": "Workspace",
    "gpuLeft": "Machine time left",
    "deadline": "Deadline",
    "deadlineBeforeLaunch": "5 days from your first Launch",
    "deadlineBeforeLaunchDays": "{{days}} days from your first Launch",
    "menu": {
      "signedInAs": "Signed in as",
      "language": "Language",
      "logout": "Sign out"
    },
    "status": {
      "ready": {
        "title": "Your machine is ready to launch",
        "body": "Launch when you’re ready. Your {{days}}-day window starts at first launch; machine time starts when the workspace is ready.",
        "reopenedBody": "Your assignment is open again. You can launch when ready. The deadline is {{deadline}}."
      },
      "starting": {
        "title": "Starting your GPU…",
        "body": "This usually takes {{range}}. You can leave this page; we can email you when it is ready."
      },
      "booting": {
        "title": "Starting Isaac Sim…",
        "body": "The machine is up and Isaac Sim is loading. This usually takes {{range}}. Machine time does not count until it is ready."
      },
      "running": {
        "title": "Your machine is running",
        "body": "We recommend pausing when you step away to save machine time."
      },
      "grace": {
        "title": "Your machine time is over.",
        "body": "The machine will shut down automatically in {{mmss}}. Save your work now.",
        "card": "Save your work now. Pause and Resume are no longer available; the machine will stop automatically when the countdown ends.",
        "startupCard": "Your time limit has been reached. Applications are still starting; access remains unavailable until they are ready. You can finish now.",
        "startupBody": "The machine will stop automatically in {{mmss}}. Pause is unavailable during this final countdown."
      },
      "stopping": {
        "title": "Pausing…",
        "body": "Your files are kept. You can resume any time within your window."
      },
      "paused": {
        "title": "Paused",
        "body": "Machine time is not counting. Resuming takes about {{range}}."
      },
      "error": {
        "title": "Something went wrong",
        "body": "The machine did not start correctly. We've been notified. You can try again.",
        "start_capacity": {
          "title": "No GPU is available right now",
          "body": "The cloud zone that holds your machine has no free GPU at the moment. This usually clears within minutes, sometimes a few hours. Your files are safe and machine time is not counting."
        },
        "start_provider": {
          "title": "The machine could not start",
          "body": "The cloud provider rejected the start request. We've been notified. You can try again in a few minutes."
        },
        "start_timeout": {
          "title": "The machine is taking too long to start",
          "body": "The start was accepted but the machine did not come up within 15 minutes. We've been notified. You can try again."
        },
        "setup_failed": {
          "title": "Workspace setup did not complete",
          "body": "The machine started but its setup failed. We've been notified; please contact HR before trying again."
        }
      },
      "ended": {
        "title": "Your session is closed. Thank you.",
        "body": "You used {{used}} of {{total}} hours of machine time."
      },
      "endedReason": {
        "finished": "You finished the session.",
        "budget_exhausted": "Your machine time has been used up.",
        "window_expired": "Your assignment time limit ended.",
        "revoked": "This session was closed by the organisers.",
        "session_expired": "Your continuous session ended.",
        "deadline_expired": "The fixed deadline was reached."
      },
      "provisioning": {
        "title": "Your machine is being prepared",
        "body": "Check back shortly."
      },
      "preparing": {
        "title": "Your workspace is being prepared",
        "body": "Initial preparation must finish before you can launch. Your machine time allowance and assignment window have not started. Contact HR if preparation is paused."
      },
      "workspaceStarting": {
        "title": "Starting workspace…",
        "body": "The machine is up and VS Code is starting. This usually takes {{range}}. GPU allowance starts when the workspace is ready."
      }
    },
    "actions": {
      "launch": "Launch",
      "pause": "Pause",
      "resume": "Resume",
      "finish": "Finish",
      "finishNow": "Finish now",
      "retry": "Try again",
      "working": "Working…"
    },
    "confirm": {
      "launch": {
        "title": "Launch your machine?",
        "body": "This starts your {{days}}-day window. Your {{hours}} hours of machine time count only while the machine is running and the workspace is ready.",
        "ok": "Launch",
        "reopenedBody": "Launch the workspace now? Your deadline remains {{deadline}}. Machine time counts only while the machine is running and the workspace is ready."
      },
      "pause": {
        "title": "Pause your machine?",
        "body": "The machine stops and your files are kept. Resuming takes about {{range}}.",
        "ok": "Pause",
        "startupBody": "Stop this startup attempt? Your files are kept and you can resume later. Machine time is not counting, but the assignment deadline still applies."
      },
      "finish": {
        "title": "Finish your session?",
        "body": "You won't be able to resume. The machine stops and your session is closed.",
        "ok": "Finish"
      }
    },
    "stepper": {
      "requesting_gpu": "Requesting GPU",
      "booting": "Booting",
      "starting_isaac": "Starting Isaac Sim",
      "ready": "Ready",
      "elapsed": "Elapsed {{time}}",
      "expected": "usually {{range}}",
      "starting_workspace": "Starting workspace"
    },
    "notify": {
      "label": "Email me when it's ready",
      "on": "We'll email you when the machine is ready."
    },
    "access": {
      "title": "Access",
      "vscode": "Open VS Code",
      "viewer": "Open Isaac Sim viewer",
      "vscodePassword": "VS Code password",
      "viewerUser": "Viewer user",
      "viewerPassword": "Viewer password",
      "oneTab": "Keep one viewer tab open at a time. A second tab disconnects the first.",
      "troubleTitle": "Connection problems?",
      "trouble": {
        "udp": "The viewer streams video over UDP port 47998. Some office and campus networks block it; a phone hotspot usually works.",
        "refresh": "If the viewer shows a black screen, refresh the tab once Isaac Sim has finished loading.",
        "vscode": "VS Code works over regular HTTPS-like traffic and keeps working even when the viewer cannot connect.",
        "hr": "Still stuck? Contact HR through the email thread you already have."
      },
      "recordingNotice": "Your prompts and visible replies sent through managed OpenCode are recorded for authorized assessment reviewers under the existing retention policy. This is a partial record; internal reasoning, tool outputs and personal endpoints are excluded."
    },
    "timeline": {
      "title": "Activity",
      "empty": "No activity yet.",
      "launched": "Launched",
      "paused": "Paused",
      "resumed": "Resumed",
      "finished": "Finished",
      "notice_budget": "{{hours}} h of machine time left",
      "notice_budget_zero": "Machine time is over",
      "notice_window": "{{hours}} h to your deadline",
      "grace_started": "10-minute shutdown started",
      "auto_paused": "Auto-paused",
      "extended_budget": "Machine time extended by {{minutes}} min",
      "extended_window": "Window extended by {{days}} days",
      "error": "Error",
      "ready": "Machine ready",
      "auto_retry": "Automatic retry #{{attempt}}"
    },
    "toast": {
      "budget": "{{hours}} hours of machine time left",
      "budgetZero": "Your machine time is over. The machine will shut down in 10 minutes.",
      "window": "{{hours}} hours to your deadline",
      "ready": "Your machine is ready"
    },
    "used": "{{used}} h used",
    "usedOf": "{{used}} of {{total}} h used",
    "live": "Status: {{state}}",
    "sessionEnded": "Your session has ended. Open your invite link again.",
    "gpuPaused": "pause",
    "startupActions": "Pause cancels this startup attempt and keeps your files; you can resume later. Finish ends the assignment and prevents resuming. Machine time is not counting during startup; the assignment deadline still applies.",
    "autoRetry": {
      "label": "Retry automatically",
      "on": "We'll request the machine again every few minutes while your deadline allows and stop as soon as it starts. You can leave this page.",
      "off": "Turn this on to keep trying without staying on this page."
    },
    "sessionLimit": "Continuous session: {{minutes}} minutes from the first Launch of this attempt, including pauses.",
    "cutoffReached": "The assignment time limit has ended. Workspace access is closed.",
    "cutoffCleanup": "The machine is shutting down. This does not extend workspace access."
  },
  "expired": {
    "pageTitle": "Session ended",
    "window": {
      "title": "The assessment window has ended",
      "body": "The 5-day window that started at your first Launch is over. Thank you for taking part."
    },
    "revoked": {
      "title": "This session was closed",
      "body": "The organisers closed this session."
    },
    "session": {
      "title": "You are signed out",
      "body": "Open your invite link from HR again to sign back in."
    },
    "default": {
      "title": "This session is no longer available",
      "body": "Thank you for taking part."
    }
  },
  "dev": {
    "states": "States",
    "hint": "Fixture data · no backend · dev only",
    "title": "Candidate preview",
    "admin": "Admin preview",
    "guide": "Design guide",
    "context": "Current workspace flow: secure links, automatic preparation, and machine time that pauses during startup. Sample data; actions stay in this preview.",
    "choose": "Choose a scenario",
    "action": "Preview: {{action}}",
    "localOnly": "This is a demonstration. No workspace was opened or changed.",
    "signOut": "Sign out",
    "emails": "Email designs",
    "overview": "Feature previews",
    "overviewHint": "Review the current assignment types and AI states with synthetic data. Changes stay in this page and disappear when you reload.",
    "gates": "Preview only. Actual access depends on server authorization, current consent, an active attempt, time limits and available budget. No workspace, candidate account or provider is accessed here.",
    "kind": "Assignment type",
    "mode": "Assessment state",
    "writtenTitle": "Visual grasping integration",
    "questionSample": "# Reliable delivery\n\nDesign a queue for document processing. Explain duplicate delivery, retries, and visibility timeouts.\n\n## Your approach\n\nDescribe the tradeoffs and include pseudocode where useful.",
    "answerSample": "## System architecture\n\nI would separate perception, grasp planning and motion control, with clear interfaces and safety checks between each stage.",
    "writtenHint": "Edit, autosave and submit this sample answer locally; the real interface uses guarded revisions and immutable submission snapshots.",
    "gpuHint": "VS Code only · AWS g6e.2xlarge · any region · 128 GB disk. A 90-minute continuous session and the earlier effective cutoff apply independently of GPU runtime.",
    "isaacHint": "VS Code and Isaac Sim viewer share the same protected workspace. Machine time pauses when stopped; continuous session and deadline clocks keep running.",
    "gpuTitle": "GPU coding take-home",
    "isaacTitle": "Isaac Sim take-home",
    "reset": "Reset sample",
    "chatTitle": "AI conversation and access states",
    "chatHint": "Synthetic replies only. Live requests require an allowed compatible model and zero data retention (ZDR); BYOK is optional. Conversations, usage and interrupted replies use the same display components as the candidate and admin views.",
    "chatSample": "Use an **idempotency key** per job. Commit the result before acknowledging delivery.\n\n```typescript\nawait processOnce(job.id, job.payload);\n```",
    "aiState": "AI access state",
    "replyStatus": "Stored reply status",
    "mockReply": "This is a local sample reply. Nothing was sent to an AI provider.",
    "mockSend": "Send sample message",
    "toggleNotice": "Toggle readiness notification",
    "modes": {
      "active": "Active",
      "finished": "Submitted",
      "expired": "Expired"
    },
    "aiStates": {
      "enabled": "Enabled sample",
      "ai_disabled": "AI disabled",
      "ai_budget_exhausted": "Budget exhausted",
      "window_over": "Time expired",
      "accounting_pending": "Accounting pending"
    },
    "workspacePreview": "Synthetic preview · Back to previews",
    "openWorkspace": "Open full workspace preview"
  },
  "feedback": {
    "pageTitle": "Assignment finished",
    "finished": "Thank you for your time. Your workspace session has ended. You can close this page whenever you’re ready.",
    "title": "How was the experience?",
    "hint": "Optional · Tell us about the process, from the invitation to using the workspace. The hiring team can read your feedback. Please leave out passwords and other sensitive information.",
    "label": "Your feedback (optional)",
    "placeholder": "What worked well? What could we make clearer or easier?",
    "send": "Send feedback",
    "skip": "Skip",
    "skipped": "You’re all done. You can close this page.",
    "thanks": "Thank you for your feedback. You can close this page.",
    "error": "We couldn’t save or load your feedback. Please try again.",
    "unavailable": "Feedback is no longer available for this session. You can close this page.",
    "loading": "Loading optional feedback…",
    "retry": "Try again"
  },
  "assessment": {
    "label": "Written assessment",
    "answer": "Answer",
    "question": "Question",
    "edit": "Edit",
    "preview": "Preview",
    "hide": "Hide {{pane}}",
    "show": "Show {{pane}}",
    "swap": "Swap sides",
    "resize": "Resize question and answer panes",
    "panes": "Assessment panes",
    "empty": "No answer yet.",
    "count": "{{words}} words · {{chars}} characters",
    "copy": "Copy answer",
    "submit": "Submit answer",
    "submitTitle": "Submit your answer?",
    "submitBody": "Your latest changes will be saved first. Submission is final for this attempt. You can read the completed answer for 24 hours.",
    "readOnly": "Read only",
    "active": "In progress",
    "readOnlyHint": "This attempt has ended. The saved answer is read only. Unsaved local text can still be copied.",
    "saving": "Saving…",
    "unsaved": "Unsaved changes",
    "saved": "Saved at {{time}}",
    "ready": "Changes save automatically after 2 seconds or when you leave the editor. Ctrl/⌘ + S saves now.",
    "tooLarge": "Answer exceeds the 200 KB limit. Shorten it before saving.",
    "conflict": "The saved version changed. Your text is preserved; choose how to continue.",
    "conflictTitle": "Resolve conflicting versions",
    "localVersion": "Your local text",
    "serverVersion": "Current saved text",
    "keepLocal": "Keep my text and save again",
    "useServer": "Use saved version",
    "questionChanged": "The question changed. Review the updated question before saving or submitting.",
    "attemptChanged": "This attempt was replaced. Copy any unsaved text before reopening your invitation.",
    "locked": "This session can no longer save. Copy any unsaved text and check your invitation.",
    "saveError": "Could not save. Your text is still here; retry before leaving.",
    "submitError": "Submission was not confirmed. Retry to check or complete the same submission.",
    "retrySave": "Retry save",
    "openError": "The assessment could not be opened. Retry or contact HR.",
    "completedUnavailable": "The completed-answer link has expired or is unavailable.",
    "intro": "Read the question and write your answer here in the browser. Your clock starts when the assessment opens after consent.",
    "workspace": "Browser-based answer",
    "workspaceHelp": "No GPU or virtual machine is created.",
    "window": "{{days}} days from opening the assessment",
    "windowHelp": "The earliest window, continuous-session or fixed-deadline limit ends editing.",
    "submission": "Submit your answer here",
    "submissionHelp": "Autosave keeps a draft. Submit explicitly when your answer is complete.",
    "sessionLimit": "Continuous session: {{minutes}} minutes from opening this attempt.",
    "savedLocally": "Saved in this tab · Waiting to sync",
    "restored": "Recovered unsynced typing from this tab. Keep the tab open until server saving completes."
  },
  "chat": {
    "title": "AI chat",
    "open": "Open AI chat",
    "back": "Back to assignment",
    "privacy": "Your browser conversations are saved for assessment review. Do not include secrets or unrelated personal information. AI replies may be inaccurate; you remain responsible for your answer.",
    "loading": "Loading conversations…",
    "unknown": "Unknown",
    "usage": "{{used}} used of {{limit}} · {{left}} remaining",
    "unsettled": "Some requests are awaiting final usage accounting.",
    "priceHint": "Usage may arrive after a reply. Different models have different prices.",
    "model": "Model for new conversation",
    "modelHint": "Choose any available ZDR text-chat model for a new conversation. Availability depends on the provider and administrator’s BYOK policy.",
    "new": "New conversation",
    "newTitle": "New conversation",
    "conversations": "Conversations",
    "moreConversations": "More conversations",
    "rename": "Rename conversation",
    "empty": "Create a conversation to begin.",
    "olderMessages": "Load older messages",
    "historyError": "Conversation history could not be loaded.",
    "streaming": "Replying…",
    "stop": "Stop reply",
    "retry": "Edit and retry",
    "composer": "Message",
    "composerHint": "Ctrl / ⌘ + Enter to send. Up to 64 KB per message.",
    "send": "Send message",
    "copy": "Copy message",
    "truncated": "This stored message was truncated.",
    "errors": {
      "ai_disabled": "AI is not enabled for this assignment.",
      "ai_budget_exhausted": "The AI budget has been used. Contact the assessment administrator.",
      "window_over": "The assessment time has ended. AI is now unavailable.",
      "consent_required": "Accept the current agreement before using AI.",
      "assignment_not_active": "AI is available only during an active assessment.",
      "accounting_pending": "Usage is being reconciled. Try again shortly.",
      "stale_attempt": "This assessment attempt changed. Reload before continuing.",
      "conversation_busy": "A reply is already running in this conversation.",
      "rate_limited": "Too many requests. Please wait before retrying.",
      "incomplete_stream": "The connection ended before the reply finished. Saved messages are shown below; review them before retrying.",
      "ai_unavailable": "AI is temporarily unavailable. Your assessment answer is unaffected.",
      "byok_verification_required": "This model has no verified BYOK-only route under the administrator’s policy. Choose another model or contact the administrator.",
      "catalog_unavailable": "The current ZDR model catalog could not be verified. Please retry shortly.",
      "model_not_allowed": "This model is no longer in the compatible ZDR catalog. Start a conversation with another model.",
      "provider_unavailable": "No response is available from the selected provider. Try again or choose another ZDR model."
    },
    "roles": {
      "user": "You",
      "assistant": "Assistant",
      "tool": "Tool",
      "system_note": "System note"
    },
    "status": {
      "streaming": "Replying",
      "complete": "Complete",
      "error": "Failed",
      "aborted": "Stopped",
      "incomplete": "Incomplete"
    },
    "updated": "Updated {{time}}"
  }
}
